import React, { useEffect, useRef } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
  StatusBar,
  Platform,
  useWindowDimensions,
  Animated,
} from 'react-native';
import {
  Button,
  Logo,
  DualPaneLayout,
  BrandWireframe,
  colors,
  spacing,
  typography
} from '../components';
import { LanguageToggle } from '../components/LanguageToggle';
import { useAppContext } from '../contexts/AppContext';
import { useLocale } from '../contexts/LocaleContext';
import { useTypography } from '../hooks/useTypography';
import { useThemedStyles } from '../hooks/useThemedStyles';

const isWeb = Platform.OS === 'web';

export const WelcomeScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { handleGetStarted } = useAppContext();
  const { t } = useLocale();
  const typo = useTypography();
  const { width, height } = useWindowDimensions();
  const logoPosition = useRef(new Animated.Value(0)).current;
  const logoOpacity = useRef(new Animated.Value(0)).current;
  const taglineOpacity = useRef(new Animated.Value(0)).current;
  const taglineTranslateY = useRef(new Animated.Value(30)).current;
  const subtitleOpacity = useRef(new Animated.Value(0)).current;
  const subtitleTranslateY = useRef(new Animated.Value(30)).current;
  const buttonOpacity = useRef(new Animated.Value(0)).current;
  const buttonScale = useRef(new Animated.Value(0.8)).current;

  useEffect(() => {
    // Animation sequence
    const animationSequence = async () => {
      // 1. Logo appears and moves up
      Animated.parallel([
        Animated.timing(logoOpacity, {
          toValue: 1,
          duration: 800,
          useNativeDriver: true,
        }),
        Animated.timing(logoPosition, {
          toValue: isWeb ? -height * 0.1 : -height * 0.15, // Less movement on web
          duration: 1200,
          delay: 200,
          useNativeDriver: true,
        }),
      ]).start();

      // 2. Tagline appears
      Animated.parallel([
        Animated.timing(taglineOpacity, {
          toValue: 1,
          duration: 600,
          useNativeDriver: true,
        }),
        Animated.timing(taglineTranslateY, {
          toValue: 0,
          duration: 600,
          useNativeDriver: true,
        }),
      ]).start();

      // 3. Subtitle appears
      setTimeout(() => {
        Animated.parallel([
          Animated.timing(subtitleOpacity, {
            toValue: 1,
            duration: 600,
            useNativeDriver: true,
          }),
          Animated.timing(subtitleTranslateY, {
            toValue: 0,
            duration: 600,
            useNativeDriver: true,
          }),
        ]).start();
      }, 400);

      // 4. Button appears with scale effect
      setTimeout(() => {
        Animated.parallel([
          Animated.timing(buttonOpacity, {
            toValue: 1,
            duration: 600,
            useNativeDriver: true,
          }),
          Animated.spring(buttonScale, {
            toValue: 1,
            tension: 100,
            friction: 8,
            useNativeDriver: true,
          }),
        ]).start();
      }, 800);
    };

    animationSequence();
  }, []);



  return (
    <SafeAreaView style={styles.container}>
      <BrandWireframe style={styles.wireframeBackground} source={require('../../assets/images/sendifier-hero.png')} />
      <View style={styles.scrim} />
      <View style={styles.languageToggle}>
        <LanguageToggle />
      </View>
      {/* Hero Section */}
      <View style={styles.heroSection}>
        <Animated.View 
          style={[
            styles.heroLogoContainer,
            {
              opacity: logoOpacity,
              transform: [{ translateY: logoPosition }],
            }
          ]}
        >
          <Logo size={isWeb ? "large" : "xlarge"} variant="white" />
        </Animated.View>

        <Animated.Text
          style={[
            styles.heroTagline,
            { fontFamily: typo.fontFamily.bold, maxWidth: isWeb ? 640 : width * 0.9 },
            {
              opacity: taglineOpacity,
              transform: [{ translateY: taglineTranslateY }],
            }
          ]}
        >
          {t('auth.welcomeTagline')}
        </Animated.Text>

        <Animated.Text
          style={[
            styles.heroSubtitle,
            { fontFamily: typo.fontFamily.primary, maxWidth: isWeb ? 640 : width * 0.85 },
            {
              opacity: subtitleOpacity,
              transform: [{ translateY: subtitleTranslateY }],
            }
          ]}
        >
          {t('auth.welcomeSubtitle')}
        </Animated.Text>

        <Animated.View
          style={{
            opacity: buttonOpacity,
            transform: [{ scale: buttonScale }],
          }}
        >
          <Button
            title={t('auth.getStarted')}
            onPress={handleGetStarted}
            variant="accent"
            size={isWeb ? "md" : "lg"}
            style={{ minWidth: isWeb ? 180 : width * 0.8, maxWidth: isWeb ? 200 : 300 }}
          />
        </Animated.View>
      </View>
    </SafeAreaView>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.primary,
    position: isWeb ? 'relative' : undefined,
    overflow: isWeb ? 'hidden' : undefined,
  },
  languageToggle: {
    position: 'absolute',
    top: isWeb ? 16 : 48,
    right: 16,
    zIndex: 100,
  },
  wireframeBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  // Darkens the busy hero photo enough for the white logo/tagline/button stack on top of it
  // to stay legible, without going as flat/heavy as the old dark scrim.
  scrim: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(20, 12, 2, 0.45)',
  },
  heroSection: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: isWeb ? spacing.xl : spacing.screenPadding,
    paddingVertical: isWeb ? spacing.xl : spacing.xl * 2,
    maxWidth: isWeb ? 800 : undefined, // Limit width on web
    alignSelf: isWeb ? 'center' : undefined, // Center on web
  },
  heroLogoContainer: {
    marginBottom: isWeb ? spacing.xl : spacing.xl * 2,
    alignItems: 'center',
  },
  heroTagline: {
    fontSize: isWeb ? typography.fontSize['2xl'] : typography.fontSize['3xl'],
    fontWeight: '700', // bold
    lineHeight: isWeb ? 32 : 36,
    letterSpacing: typography.letterSpacing.tight,
    color: colors.white,
    textAlign: 'center',
    marginBottom: isWeb ? spacing.md : spacing.lg,
  },
  heroSubtitle: {
    fontSize: isWeb ? typography.fontSize.base : typography.fontSize.lg,
    fontWeight: '400', // normal
    lineHeight: isWeb ? 24 : 28,
    color: colors.white,
    textAlign: 'center',
    marginBottom: isWeb ? spacing.xl : spacing.xl * 2,
    opacity: 0.9,
  },
});
